import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import teamPhoto from '../assets/team_photo.jpg';

const TeamSection = () => {
  const navigate = useNavigate();
  const [isTeamSectionVisible, setIsTeamSectionVisible] = useState(false);
  const teamSectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === teamSectionRef.current && entry.isIntersecting) {
            setIsTeamSectionVisible(true);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
        rootMargin: '0px 0px -10px 0px' // Start animation much earlier before section comes into view
      }
    );

    const currentRef = teamSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={teamSectionRef}
      className="section-seamless"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: 'transparent'
      }}
    >
      <div style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'stretch'
      }}
      className="team-grid"
      data-mobile-order="image-first">
        {/* Left Content */}
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
          padding: '80px 60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          color: 'white',
          opacity: isTeamSectionVisible ? 1 : 0,
          transform: isTeamSectionVisible ? 'translateX(0)' : 'translateX(-30px)',
          transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDelay: isTeamSectionVisible ? '0.2s' : '0s'
        }}>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '200',
            margin: '0 0 20px 0',
            letterSpacing: '0.15em',
            opacity: isTeamSectionVisible ? 1 : 0,
            transform: isTeamSectionVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: isTeamSectionVisible ? '0.3s' : '0s'
          }}>
            BLUE STAR ROWING TEAM
          </h2>
          <h3 style={{
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
            fontWeight: '300',
            margin: '0 0 60px 0',
            color: '#60a5fa',
            letterSpacing: '0.1em',
            opacity: isTeamSectionVisible ? 1 : 0,
            transform: isTeamSectionVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: isTeamSectionVisible ? '0.4s' : '0s'
          }}>
            INTRODUCTIONS
          </h3>

          <p style={{
            fontSize: '24px',
            fontWeight: '400',
            lineHeight: '1.4',
            marginBottom: '60px',
            color: '#ffffff',
            maxWidth: '520px'
          }}>
            Who actually are we and who thought this was a good idea!
          </p>

          <div style={{
            width: '120px',
            height: '2px',
            backgroundColor: '#60a5fa',
            margin: '0 0 60px 0'
          }}></div>

          <p style={{
            fontSize: '18px',
            lineHeight: '1.6',
            color: '#cbd5e1',
            marginBottom: '60px',
            maxWidth: '520px'
          }}>
            This section breaks down our personal backgrounds and why we are completing the World's Toughest Row. We're not elite athletes or pro adventures. We're three mates who backed ourselves to take on something way bigger than us, and we're figuring it out as we go.
          </p>

          <button
            style={{
              background: 'transparent',
              color: 'white',
              padding: '16px 40px',
              borderRadius: '2px',
              border: '2px solid white',
              fontSize: '14px',
              letterSpacing: '0.15em',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              alignSelf: 'flex-start'
            }}
            onClick={() => navigate('/about')}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#0f172a';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}
          >
            READ MORE
          </button>
        </div>

        {/* Right Image */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundImage: `url(${teamPhoto})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
          opacity: isTeamSectionVisible ? 1 : 0,
          transform: isTeamSectionVisible ? 'translateX(0) scale(1)' : 'translateX(30px) scale(0.95)',
          transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDelay: isTeamSectionVisible ? '0.4s' : '0s'
        }}>
          {/* Overlay gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(15, 23, 42, 0.3), rgba(15, 23, 42, 0.1), transparent)'
          }}></div>

          {/* Red "VIDEO HERE" button in top right */}
          <button
            onClick={() => {
              const videoSection = document.querySelector('.video-section');
              if (videoSection) {
                videoSection.scrollIntoView({ behavior: 'smooth' });
                const video = videoSection.querySelector('video');
                if (video) {
                  setTimeout(() => video.play(), 1000);
                }
              }
            }}
            style={{
            position: 'absolute',
            top: '-31px',
            right: '0px',
            background: '#dc2626',
            color: 'white',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#b91c1c';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#dc2626';
          }}>
            VIDEO HERE
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamSection;